import { dayKey } from './format.js'

/**
 * Resolve a Login Activity row's displayed session pair from a
 * representative user's most-recent login + logout instants.
 *
 * The Login Time and Logout Time must belong to the SAME user's SAME-DAY
 * session — never a login from one day paired with a logout from another.
 * The logout is therefore kept ONLY when it is:
 *
 *   1. on the SAME calendar day as the login, evaluated in the ACTIVE
 *      DISPLAY TIMEZONE (the same zone `formatDateTime` renders in, via the
 *      shared `dayKey` helper) — so the two dates the row shows always line
 *      up, regardless of the viewer's browser zone; AND
 *   2. at or after the login instant.
 *
 * Otherwise the session is treated as still open: the stored logout is from
 * an earlier day's session (e.g. an overnight sign-in whose logout lands the
 * next day) or the user has since logged in again. In that case `logoutAt`
 * is null and the caller renders "Not logged out" instead of pairing a
 * mismatched logout.
 *
 * Pure and side-effect free. `loginIso` / `logoutIso` are ISO instant
 * strings (or null).
 *
 * @returns {{ loginAt: string|null, logoutAt: string|null }}
 */
export function resolveSessionPair(loginIso, logoutIso) {
  if (!loginIso) return { loginAt: null, logoutAt: null }
  const loginDay = dayKey(loginIso)
  // Unparseable login → nothing to show.
  if (!loginDay) return { loginAt: null, logoutAt: null }
  if (!logoutIso) return { loginAt: loginIso, logoutAt: null }

  const logoutDay = dayKey(logoutIso)
  const atOrAfterLogin =
    new Date(logoutIso).valueOf() >= new Date(loginIso).valueOf()
  const sameDaySession =
    Boolean(logoutDay) && logoutDay === loginDay && atOrAfterLogin

  return { loginAt: loginIso, logoutAt: sameDaySession ? logoutIso : null }
}
